"use client";

import { lazy, Suspense, useEffect, useRef, useState } from "react";

const HomeDeferredContent = lazy(() =>
  import("@/wp/pages/HomeDeferredContent").then((module) => ({
    default: module.HomeDeferredContent,
  })),
);

const DEFERRED_HASHES = new Set([
  "#solucoes",
  "#como-funciona",
  "#integracoes",
  "#faq",
]);

/**
 * Mantém as seções distantes da dobra fora do bundle inicial. Um clique no
 * menu continua funcionando: o hash força o carregamento e o destino é
 * reposicionado assim que o chunk termina de montar.
 */
export function HomeDeferredSections() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const loadFromHash = () => {
      if (DEFERRED_HASHES.has(window.location.hash)) setShouldLoad(true);
    };
    loadFromHash();
    window.addEventListener("hashchange", loadFromHash);

    const sentinel = sentinelRef.current;
    if (!sentinel || shouldLoad) {
      return () => window.removeEventListener("hashchange", loadFromHash);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "700px" },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      window.removeEventListener("hashchange", loadFromHash);
    };
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || !DEFERRED_HASHES.has(window.location.hash)) return;

    let attempts = 0;
    const scrollToHash = () => {
      const target = document.querySelector(window.location.hash);
      if (target) {
        target.scrollIntoView();
        return;
      }
      if (attempts++ < 30) window.setTimeout(scrollToHash, 50);
    };
    scrollToHash();
  }, [shouldLoad]);

  return (
    <div ref={sentinelRef} className={shouldLoad ? undefined : "min-h-[70vh]"} aria-busy={!shouldLoad}>
      {shouldLoad && (
        <Suspense fallback={<div className="min-h-[70vh]" aria-hidden />}>
          <HomeDeferredContent />
        </Suspense>
      )}
    </div>
  );
}
